/**
 * This file is a part of Amblique's projects.
 *
 * Copyright © Amblique - All Rights Reserved.
 *
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential.
 */

/**
 * AddressAutocompleteUI
 *
 * A pure UI component for displaying address autocomplete predictions as a floating dropdown.
 * This component is designed to be reusable across the application wherever address
 * autocomplete functionality is needed.
 *
 * Features:
 * - Floating dropdown overlay (doesn't push content down)
 * - Themeable via Chakra UI theme system
 * - Displays main text and secondary text for each prediction
 * - Handles click events via callback prop
 *
 * @example
 * <Box position="relative">
 *   <Input value={input} onChange={handleChange} />
 *   <AddressAutocompleteUI
 *     predictions={autocomplete.predictions}
 *     onSelectPrediction={handleSelectPrediction}
 *   />
 * </Box>
 */

import React from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Text,
  VStack,
  useMultiStyleConfig,
} from '@salesforce/retail-react-app/app/components/shared/ui';

const AddressAutocompleteUI = ({
  predictions = [],
  onSelectPrediction,
  isVisible = true,
  variant,
  size,
  ...rest
}) => {
  const styles = useMultiStyleConfig('AddressAutocompleteUI', {variant, size, ...rest});

  if (!isVisible || !predictions || predictions.length === 0) {
    return null;
  }

  const isPdp = variant === 'pdp';

  return (
    <Box {...styles.container} {...rest}>
      <VStack {...styles.list}>
        {predictions.map((prediction) => (
          <Box
            key={prediction.place_id}
            {...styles.item}
            onClick={() => onSelectPrediction?.(prediction)}
          >
            {isPdp ?
              (
                <Text {...styles.mainText}>
                  {prediction.description ||
                    [
                      prediction.structured_formatting?.main_text,
                      prediction.structured_formatting?.secondary_text,
                    ]
                      .filter(Boolean)
                      .join(', ')}
                </Text>
              ) :
              (
                <>
                  <Text {...styles.mainText}>
                    {prediction.structured_formatting?.main_text || prediction.description}
                  </Text>
                  {prediction.structured_formatting?.secondary_text ?
                    (
                      <Text {...styles.secondaryText}>
                        {prediction.structured_formatting.secondary_text}
                      </Text>
                    ) :
                    null}
                </>
              )}
          </Box>
        ))}
      </VStack>
    </Box>
  );
};

AddressAutocompleteUI.displayName = 'AddressAutocompleteUI';

AddressAutocompleteUI.propTypes = {
  /**
   * Array of prediction objects from Google Places Autocomplete API
   * Each prediction should have:
   * - place_id: string (unique identifier)
   * - description: string (full address)
   * - structured_formatting: { main_text: string, secondary_text: string }
   */
  predictions: PropTypes.arrayOf(
    PropTypes.shape({
      place_id: PropTypes.string.isRequired,
      description: PropTypes.string,
      structured_formatting: PropTypes.shape({
        main_text: PropTypes.string,
        secondary_text: PropTypes.string,
      }),
    })
  ),

  /**
   * Callback function called when a prediction is selected
   * @param {Object} prediction - The selected prediction object
   */
  onSelectPrediction: PropTypes.func,

  /**
   * Controls visibility of the dropdown
   * Useful for programmatic show/hide without unmounting
   */
  isVisible: PropTypes.bool,

  variant: PropTypes.string,

  size: PropTypes.string,
};

export default AddressAutocompleteUI;
